import React, { useCallback, useEffect, useState } from 'react'
import { IonModal } from '@ionic/react'
import { Icon } from '@iconify/react'
import { useLocation } from 'react-router'
import dsLogo from '../../../assets/images/ds-logo.svg'
import leavesTopLeft from '../../../assets/images/new-app/leaves-top-left.png'
import leavesBottomRight from '../../../assets/images/new-app/leaves-bottom-right.png'
import leafDivider from '../../../assets/images/new-app/leaf-divider.png'
import config from '../../../settings/config'
import './NewAppModal.css'

// From this date (device local time) the popup becomes a blocking screen.
const SHUTDOWN_DATE = new Date(2027, 0, 1)

const IOS_URL = 'https://apps.apple.com/it/app/id6746050415'
const ANDROID_URL = `https://play.google.com/store/apps/details?id=${config.env.packageName}`

// Stores the local day (YYYY-MM-DD) on which the user chose "Continua con questa versione".
const DISMISSED_KEY = 'ds_new_app_dismissed_on'

const todayKey = () => {
	const d = new Date()
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	return `${d.getFullYear()}-${mm}-${dd}`
}

// ?newAppMandatory previews the blocking version before the shutdown date.
const isMandatory = () =>
	new Date() >= SHUTDOWN_DATE ||
	new URLSearchParams(window.location.search).has('newAppMandatory')

const getDismissedDay = () => {
	try {
		return localStorage.getItem(DISMISSED_KEY)
	} catch (e) {
		return null
	}
}

const shouldShow = () => isMandatory() || getDismissedDay() !== todayKey()

const NewAppModal = () => {
	const location = useLocation()
	const isImpersonate = localStorage.getItem('adoc_i') === 'true'

	const [isOpen, setIsOpen] = useState(() => !isImpersonate && shouldShow())
	const [mandatory, setMandatory] = useState(isMandatory)

	const refresh = useCallback(() => {
		if (isImpersonate) return
		setMandatory(isMandatory())
		if (shouldShow()) setIsOpen(true)
	}, [isImpersonate])

	// Re-check on navigation and when the app comes back to the foreground,
	// so a tab left open overnight shows the popup on the new day.
	useEffect(() => {
		refresh()
	}, [location.pathname, refresh])

	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState === 'visible') refresh()
		}
		document.addEventListener('visibilitychange', onVisibility)
		return () => document.removeEventListener('visibilitychange', onVisibility)
	}, [refresh])

	const continueWithThisVersion = () => {
		try {
			localStorage.setItem(DISMISSED_KEY, todayKey())
		} catch (e) {
			// Storage blocked: the popup just closes for this session.
		}
		setIsOpen(false)
	}

	return (
		<IonModal
			className="new-app-modal priority-modal"
			isOpen={isOpen}
			backdropDismiss={false}
			canDismiss={!mandatory}
			onDidDismiss={() => setIsOpen(false)}>
			<div className="new-app-modal__card">
				<img
					src={leavesTopLeft}
					alt=""
					className="new-app-modal__leaves new-app-modal__leaves--top"
				/>
				<img
					src={leavesBottomRight}
					alt=""
					className="new-app-modal__leaves new-app-modal__leaves--bottom"
				/>
				<div className="new-app-modal__body">
					<img
						src={dsLogo}
						alt="Dieta Social"
						className="new-app-modal__logo"
					/>
					<h2 className="new-app-modal__title">
						Dieta Social{' '}
						<span className="new-app-modal__nowrap">si rinnova! ❤️</span>
					</h2>
					<div className="new-app-modal__divider">
						<img src={leafDivider} alt="" />
					</div>

					{mandatory ? (
						<>
							<p>
								<strong>
									Dal 1° gennaio 2027 questa versione non è più disponibile.
								</strong>
							</p>
							<p>
								Scarica la nuova app Dieta Social per continuare il tuo
								percorso.
							</p>
						</>
					) : (
						<>
							<p>
								<strong>
									Dal 1° gennaio 2027 questa versione non sarà più disponibile.
								</strong>
							</p>
							<p>
								Scarica la nuova app Dieta Social e scopri un’esperienza ancora
								più completa!
							</p>
						</>
					)}
					<p>
						Il tuo account e il tuo abbonamento restano validi. Non devi
						registrarti di nuovo.
					</p>

					<div className="new-app-modal__stores">
						<a
							className="new-app-modal__store new-app-modal__store--ios"
							href={IOS_URL}
							target="_blank"
							rel="noopener noreferrer">
							<Icon icon="mdi:apple" width="26" />
							<span>Scarica su App Store</span>
							<Icon
								icon="mdi:arrow-right"
								width="22"
								className="new-app-modal__arrow"
							/>
						</a>
						<a
							className="new-app-modal__store new-app-modal__store--android"
							href={ANDROID_URL}
							target="_blank"
							rel="noopener noreferrer">
							<Icon icon="mdi:google-play" width="24" />
							<span>Scarica su Google Play</span>
							<Icon
								icon="mdi:arrow-right"
								width="22"
								className="new-app-modal__arrow"
							/>
						</a>
					</div>

					{!mandatory && (
						<>
							<div className="new-app-modal__or">oppure</div>
							<button
								type="button"
								className="new-app-modal__continue"
								onClick={continueWithThisVersion}>
								Continua con questa versione
							</button>
							<p className="new-app-modal__note">
								Questo messaggio compare al primo accesso di ogni giorno.
							</p>
						</>
					)}
				</div>
			</div>
		</IonModal>
	)
}

export default NewAppModal
